import { startOfDay } from 'date-fns';

import {
  TransactionPaymentMethod,
  TransactionStatus,
  TransactionType,
} from '__generated__/GQL';
import isPendingTransactionValidated from 'common/transaction/isPendingTransactionValidated';
import { formatDate, YYYY_MM_DD } from 'helpers/date';

import {
  TransactionNode,
  TransactionSection,
  TransactionsSearchConnection,
} from './TransactionList/types';

type ToTransactionListParams = {
  transactions: TransactionsSearchConnection;
  mergeIntraCompanyFundsTransfers: boolean;
};

type MergeIntraCompanyFundsTransferTransactionParams = {
  mergeIntraCompanyFundsTransfers: boolean;
  transaction: TransactionNode;
  transactions: TransactionsSearchConnection;
};

const mapTransactionStatus = (transaction: TransactionNode) => {
  if (isPendingTransactionValidated(transaction)) {
    return { ...transaction, status: TransactionStatus.Validated };
  }
  return transaction;
};

const getGroupKey = ({ status, transactionAt }: TransactionNode) => {
  return status === TransactionStatus.Pending
    ? 'PENDING'
    : formatDate(startOfDay(new Date(transactionAt)), YYYY_MM_DD);
};

const mergeIntraCompanyFundsTransferTransaction = ({
  mergeIntraCompanyFundsTransfers,
  transaction,
  transactions,
}: MergeIntraCompanyFundsTransferTransactionParams) => {
  if (
    mergeIntraCompanyFundsTransfers &&
    transaction.paymentMethod ===
      TransactionPaymentMethod.IntraCompanyFundsTransfer &&
    transaction.foreignEntityId
  ) {
    if (transaction.type === TransactionType.Payout) {
      const correspondingPayinTransaction = transactions.edges.find(
        ({ node: { foreignEntityId, foreignEntityType, type } }) =>
          foreignEntityType === 'IntraCompanyFundsTransfer' &&
          foreignEntityId === transaction.foreignEntityId &&
          type === TransactionType.Payin,
      )?.node;

      return {
        ...transaction,
        toBankAccountId: correspondingPayinTransaction?.bankAccountId,
      };
    } else {
      return null;
    }
  } else {
    return transaction;
  }
};

const sortTransactionByDate = (transactions: TransactionNode[]) => {
  return transactions.sort((transactionA, transactionB) => {
    return transactionB.transactionAt - transactionA.transactionAt;
  });
};

export const toTransactionList = ({
  mergeIntraCompanyFundsTransfers,
  transactions,
}: ToTransactionListParams): TransactionSection[] => {
  const sectionnedList = transactions.edges.reduce(
    (acc: Record<string, TransactionNode[]>, edge) => {
      const transaction = mapTransactionStatus(edge.node);

      const formattedTransaction = mergeIntraCompanyFundsTransferTransaction({
        mergeIntraCompanyFundsTransfers,
        transaction,
        transactions,
      });

      if (!formattedTransaction) {
        return acc;
      }

      const groupKey = getGroupKey(formattedTransaction);
      const group = acc[groupKey];

      return {
        ...acc,
        [groupKey]: group
          ? [...group, formattedTransaction]
          : [formattedTransaction],
      };
    },
    {},
  );

  return Object.entries(sectionnedList)
    .sort(([keyA], [keyB]) => (keyA > keyB ? -1 : 1))
    .map(([key, value]) => ({ data: sortTransactionByDate(value), key }));
};
